/**
 * Enhanced Next.js middleware with all features built-in
 */

import { NextRequest, NextResponse } from 'next/server';
import { EdgeSafeDetector } from './edge-safe-detector';
import { createStorageAdapter } from './storage';
import type { StorageAdapter, StorageConfig, AgentDetectionEvent } from './storage/types';
import type { DetectionResult, DetectionInput } from '@kya-os/agentshield-shared';
import type { EdgeAgentDetectorWrapperWithWasm } from './edge-detector-with-wasm';

/**
 * Enhanced configuration with built-in features
 */
export interface EnhancedMiddlewareConfig {
  /**
   * Storage configuration
   */
  storage?: StorageConfig;

  /**
   * Session tracking configuration
   */
  sessionTracking?: {
    enabled?: boolean;
    ttl?: number;
  };

  /**
   * Paths to skip detection
   */
  skipPaths?: string[];

  /**
   * Action when agent detected
   */
  onAgentDetected?: 'block' | 'log' | 'allow';

  /**
   * Custom handler for agent detection
   */
  onDetection?: (result: DetectionResult, context: DetectionInput) => void | Promise<void>;

  /**
   * Confidence threshold
   */
  confidenceThreshold?: number;

  /**
   * Response when blocking
   */
  blockedResponse?: {
    status?: number;
    message?: string;
  };
}

/**
 * Session tracking helper
 */
class SessionManager {
  private sessionLastActivity = new Map<string, number>();

  generateSessionId(ipAddress?: string, userAgent?: string): string {
    const now = Date.now();
    const timeWindow = Math.floor(now / (5 * 60 * 1000)); // 5-minute windows
    const baseKey = `${ipAddress || 'unknown'}:${userAgent || 'unknown'}`;
    const windowKey = `${baseKey}:${timeWindow}`;

    const lastActivity = this.sessionLastActivity.get(windowKey);
    const shouldCreateNewSession = !lastActivity || now - lastActivity > 30000;

    this.sessionLastActivity.set(windowKey, now);

    // Clean up old entries
    if (this.sessionLastActivity.size > 100) {
      const cutoff = now - 600000;
      for (const [key, time] of this.sessionLastActivity.entries()) {
        if (time < cutoff) {
          this.sessionLastActivity.delete(key);
        }
      }
    }

    const data = shouldCreateNewSession ? `${windowKey}:${now}` : `${windowKey}:${lastActivity}`;

    // Simple hash
    let hash = 0;
    for (let i = 0; i < data.length; i++) {
      const char = data.charCodeAt(i);
      hash = (hash << 5) - hash + char;
      hash = hash & hash;
    }

    return Math.abs(hash).toString(16).padStart(12, '0').substring(0, 12);
  }
}

/**
 * Create enhanced AgentShield middleware with all features
 */
export function createEnhancedAgentShieldMiddleware(config: EnhancedMiddlewareConfig = {}) {
  // Initialize storage adapter
  let storageAdapter: StorageAdapter | null = null;
  let storageInitPromise: Promise<StorageAdapter> | null = null;

  const getStorage = async () => {
    if (storageAdapter) return storageAdapter;
    if (storageInitPromise) return storageInitPromise;

    storageInitPromise = createStorageAdapter(config.storage).then((adapter) => {
      storageAdapter = adapter;
      return adapter;
    });

    return storageInitPromise;
  };

  // Initialize detector - Edge Runtime safe
  type DetectorType = EdgeSafeDetector | EdgeAgentDetectorWrapperWithWasm;
  let detector: DetectorType | null = null;
  let detectorInitPromise: Promise<void> | null = null;
  let wasmConfidenceUtils: typeof import('./wasm-confidence') | null = null;

  const getDetector = async (requestUrl?: string) => {
    if (detector) {
      return detector;
    }
    if (detectorInitPromise) {
      await detectorInitPromise;
      return detector!;
    }

    detectorInitPromise = (async () => {
      // Check if we're in Edge Runtime
      const isEdgeRuntime =
        typeof (globalThis as { EdgeRuntime?: unknown }).EdgeRuntime !== 'undefined' ||
        process.env.NEXT_RUNTIME === 'edge';

      if (isEdgeRuntime) {
        // Use Edge-safe detector (no WASM)
        if (process.env.NODE_ENV !== 'production') {
          console.debug('[AgentShield] Edge Runtime detected - using pattern detection');
        }
        detector = new EdgeSafeDetector();
      } else {
        try {
          // Try to load WASM confidence utilities first
          try {
            const wasmUtils = await import('./wasm-confidence');
            wasmConfidenceUtils = wasmUtils;
            const wasmAvailable = await wasmUtils.checkWasmAvailability();

            if (wasmAvailable) {
              // Dynamically import WASM detector only when not in Edge Runtime
              const { EdgeAgentDetectorWrapperWithWasm } = await import(
                './edge-detector-with-wasm'
              );
              if (process.env.NODE_ENV !== 'production') {
                console.debug(
                  '[AgentShield] ✅ WASM support detected - enhanced detection enabled'
                );
              }
              detector = new EdgeAgentDetectorWrapperWithWasm({ enableWasm: true });
              if (requestUrl && 'setBaseUrl' in detector) {
                detector.setBaseUrl(requestUrl);
              }
            } else {
              if (process.env.NODE_ENV !== 'production') {
                console.debug(
                  '[AgentShield] ℹ️ Using pattern-based detection (WASM not available)'
                );
              }
              detector = new EdgeSafeDetector();
            }
          } catch (wasmError) {
            // WASM utilities not available
            if (process.env.NODE_ENV !== 'production') {
              console.debug('[AgentShield] WASM utilities not available, using pattern detection');
            }
            detector = new EdgeSafeDetector();
          }
        } catch (error) {
          // Fallback to Edge-safe detector
          console.warn('[AgentShield] Failed to initialize enhanced detector, using fallback');
          detector = new EdgeSafeDetector();
        }
      }
    })();

    await detectorInitPromise;
    return detector!;
  };

  // Session manager
  const sessionManager = new SessionManager();
  const sessionTrackingEnabled = config.sessionTracking?.enabled !== false;

  // Return the middleware function
  return async (request: NextRequest): Promise<NextResponse> => {
    const { pathname } = request.nextUrl;

    // Check if path should be skipped
    if (config.skipPaths?.some((path) => pathname.startsWith(path))) {
      return NextResponse.next();
    }

    // Extract request context
    const userAgent = request.headers.get('user-agent');
    const ipAddress = request.ip ?? request.headers.get('x-forwarded-for');
    const url = new URL(request.url);
    const pathWithQuery = url.pathname + url.search;

    const context = {
      userAgent: userAgent || '',
      ipAddress: ipAddress || '',
      headers: Object.fromEntries(request.headers.entries()),
      url: pathWithQuery,
      method: request.method,
      timestamp: new Date(),
    };

    // Run detection with lazy-loaded detector (pass request URL for WASM loading)
    const activeDetector = await getDetector(request.url);
    const result = await activeDetector.analyze(context);

    // Apply WASM confidence boost if applicable
    let finalConfidence = result.confidence;
    let verificationMethod: string = result.verificationMethod || 'pattern';

    if (result.isAgent && wasmConfidenceUtils) {
      const reasons = result.reasons || [];

      if (wasmConfidenceUtils.shouldIndicateWasmVerification(result.confidence)) {
        finalConfidence = wasmConfidenceUtils.getWasmConfidenceBoost(result.confidence, reasons);
        verificationMethod = wasmConfidenceUtils.getVerificationMethod(finalConfidence, reasons);
      }
    }

    // Handle high-confidence detection
    if (result.isAgent && finalConfidence >= (config.confidenceThreshold ?? 0.7)) {
      // Store event if storage is configured and session tracking is enabled
      if (sessionTrackingEnabled) {
        const storage = await getStorage();
        const sessionId = sessionManager.generateSessionId(
          ipAddress || undefined,
          userAgent || undefined
        );

        // Create event
        const event: AgentDetectionEvent = {
          eventId: `agent_${Date.now()}_${Math.random().toString(36).substring(2, 11)}`,
          sessionId,
          timestamp: new Date().toISOString(),
          agentType: result.detectedAgent?.type || 'unknown',
          agentName: result.detectedAgent?.name || 'Unknown',
          confidence: finalConfidence,
          path: pathWithQuery,
          ...(userAgent && { userAgent }),
          ...(ipAddress && { ipAddress }),
          method: request.method,
          detectionReasons: result.reasons || [],
          verificationMethod,
          detectionDetails: {
            patterns: (result.detectedAgent as { patterns?: string[] })?.patterns,
            behaviors: (result.detectedAgent as { behaviors?: string[] })?.behaviors,
            fingerprintMatches: (result.detectedAgent as { fingerprints?: string[] })?.fingerprints,
          },
        };

        // Store event
        try {
          await storage.storeEvent(event);

          // Get or create session
          let session = await storage.getSession(sessionId);

          if (session) {
            // Update existing session
            session.lastSeen = event.timestamp;
            session.eventCount++;
            if (!session.paths.includes(pathWithQuery)) {
              session.paths.push(pathWithQuery);
            }
            session.averageConfidence =
              (session.averageConfidence * (session.eventCount - 1) + finalConfidence) /
              session.eventCount;
            if (!session.verificationMethods.includes(verificationMethod)) {
              session.verificationMethods.push(verificationMethod);
            }
          } else {
            // Create new session
            session = {
              sessionId,
              ...(ipAddress && { ipAddress }),
              ...(userAgent && { userAgent }),
              agentType: result.detectedAgent?.type || 'unknown',
              agentName: result.detectedAgent?.name || 'Unknown',
              firstSeen: event.timestamp,
              lastSeen: event.timestamp,
              eventCount: 1,
              paths: [pathWithQuery],
              averageConfidence: finalConfidence,
              verificationMethods: [verificationMethod],
            };
          }

          if (session) {
            await storage.storeSession(session);
          }
        } catch (error) {
          console.error('[AgentShield] Failed to store event:', error);
        }
      }

      // Call custom handler if provided
      if (config.onDetection) {
        await config.onDetection(result, context);
      }

      // Handle based on configuration
      switch (config.onAgentDetected) {
        case 'block': {
          const { status = 403, message = 'Access denied: AI agent detected' } =
            config.blockedResponse || {};

          const response = NextResponse.json(
            { error: message, detected: true, confidence: finalConfidence },
            { status }
          );

          // Add detection headers
          response.headers.set('x-agentshield-detected', 'true');
          response.headers.set(
            'x-agentshield-confidence',
            String(Math.round(finalConfidence * 100))
          );
          response.headers.set('x-agentshield-agent', result.detectedAgent?.name || 'Unknown');
          response.headers.set('x-agentshield-verification', verificationMethod);

          return response;
        }

        case 'log': {
          // Only log high-confidence or interesting detections
          const isInteresting =
            finalConfidence >= 0.9 ||
            result.detectedAgent?.name?.toLowerCase().includes('chatgpt') ||
            result.detectedAgent?.name?.toLowerCase().includes('perplexity') ||
            verificationMethod === 'signature';

          if (isInteresting && process.env.NODE_ENV !== 'production') {
            console.debug(`[AgentShield] 🤖 AI Agent detected (${verificationMethod}):`, {
              agent: result.detectedAgent?.name,
              confidence: `${(finalConfidence * 100).toFixed(0)}%`,
              path: pathWithQuery,
              verification: verificationMethod,
            });
          }
          break;
        }

        case 'allow':
        default:
          // Continue processing
          break;
      }
    }

    // Create response with detection headers
    const response = NextResponse.next();

    if (result.isAgent) {
      response.headers.set('x-agentshield-detected', 'true');
      response.headers.set('x-agentshield-confidence', String(Math.round(finalConfidence * 100)));
      response.headers.set('x-agentshield-agent', result.detectedAgent?.name || 'Unknown');
      response.headers.set('x-agentshield-verification', verificationMethod);

      // Add custom headers for high confidence
      if (finalConfidence > 0.9) {
        response.headers.set('x-ai-visitor', 'true');
        response.headers.set('x-ai-confidence', finalConfidence.toString());
        response.headers.set('x-ai-verification', verificationMethod);
      }
    }

    return response;
  };
}

/**
 * Export storage types for user access
 */
export type {
  StorageAdapter,
  StorageConfig,
  AgentDetectionEvent,
  AgentSession,
} from './storage/types';
